import { forwardRef } from 'react';
import { Button as ButtonPrimitive } from '@base-ui/react/button';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from 'components/banff-ui/cn';
import { Spinner } from 'components/banff-ui/Spinner';

export const buttonVariants = cva(
  "group/button inline-flex shrink-0 items-center justify-center rounded-lg border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap transition-all outline-none select-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50 active:[&:not([aria-haspopup])]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-2 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 data-[readonly=true]:pointer-events-none",
  {
    defaultVariants: {
      size: 'default',
      variant: 'default',
    },
    variants: {
      size: {
        default:
          'h-8 gap-1.5 px-2.5 has-[[data-icon=inline-end]]:pr-2 has-[[data-icon=inline-start]]:pl-2',
        icon: 'size-8',
        'icon-lg': 'size-9',
        'icon-sm': 'size-7 rounded-md',
        'icon-xs': "size-6 rounded-md [&_svg:not([class*='size-'])]:size-3",
        lg: 'h-9 gap-1.5 px-2.5 has-[[data-icon=inline-end]]:pr-2 has-[[data-icon=inline-start]]:pl-2',
        sm: "h-7 gap-1 rounded-md px-2.5 text-[0.8rem] has-[[data-icon=inline-end]]:pr-1.5 has-[[data-icon=inline-start]]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
        xs: "h-6 gap-1 rounded-md px-2 text-xs has-[[data-icon=inline-end]]:pr-1.5 has-[[data-icon=inline-start]]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
      },
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary-hover',
        destructive:
          'bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:hover:bg-destructive/30 dark:focus-visible:ring-destructive/40',
        ghost: [
          'hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-muted-hover',
          'data-[active=true]:text-primary hover:data-[active=true]:text-primary',
        ],
        link: [
          'text-primary underline-offset-4 hover:underline',
          'data-[active=true]:text-primary data-[active=true]:underline hover:data-[active=true]:text-primary',
        ],
        outline: [
          'border-border bg-background hover:bg-muted-hover hover:text-foreground aria-expanded:bg-muted-hover aria-expanded:text-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50',
          'data-[active=true]:border-primary data-[active=true]:text-primary hover:data-[active=true]:text-primary hover:data-[active=true]:bg-secondary',
        ],
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary-hover aria-expanded:bg-secondary aria-expanded:text-secondary-foreground',
        muted: [
          'bg-muted text-muted-foreground hover:bg-muted-hover aria-expanded:bg-muted aria-expanded:text-muted-foreground',
          'data-[active=true]:text-primary hover:data-[active=true]:text-primary',
        ],
      },
    },
  },
);

export interface ButtonProps
  extends ButtonPrimitive.Props, VariantProps<typeof buttonVariants> {
  loading?: boolean;
  active?: boolean;
}

/**
 * Click target for an action: submit, confirm, open, or cancel.
 * Use `loading` while the action runs and `active` when it represents the current filter or view.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      children,
      className,
      disabled,
      loading = false,
      size = 'default',
      variant = 'default',
      active = false,
      ...props
    },
    ref,
  ) {
    return (
      <ButtonPrimitive
        data-slot="button"
        data-active={active}
        type="button"
        {...props}
        ref={ref}
        aria-busy={loading || undefined}
        className={cn(buttonVariants({ className, size, variant }))}
        disabled={disabled || loading}
      >
        {loading ? <Spinner aria-hidden /> : null}
        {children}
      </ButtonPrimitive>
    );
  },
);
